define(['jQuery', 'underscore', 'library/fcse-compatibility/object', 'library/fcse-events/eventDispatcher'], function ($, _, Object, EventDispatcher) {

    // Allocate memory only once for these:
    var validShowEffects = ['show', 'fadeIn'],
        validHideEffects = ['hide', 'fadeOut'];

    /**
     * The InlineModal constructor
     *
     * @param {String} selector The selector to the inline modal(s)
     * @param {Object} [options] An optional options object
     * @param {String} [options.showMethod='fadeIn'] The jQuery method to use to
     *  show the modal. Can be one of 'show' or 'fadeIn'.
     * @param {Number} [options.showEffectDuration=300] The duration for the
     *  show effect.
     * @param {String} [options.hideMethod='fadeOut'] The jQuery method to use to
     *  hide the modal. Can be one of 'hide' or 'fadeOut'.
     * @param {Number} [options.hideEffectDuration=600] The duration for the
     *  show effect.
     * @param {String} [options.triggerEventName='mouseenter'] The event type
     *  to listen to.
     * @param {String} [options.triggerSelectorAttribute='data-trigger'] The
     *  attribute that contains the selector to the trigger element.
     * @param {Boolean} [options.hideOnLeave=true] Whether to hide the modal
     *  when the user leaves it with the mouse.
     * @param {Number} [options.hideDelay=300] If hideOnLeave is set to true,
     *  this defines the duration by which hiding is delayed
     * @param {String} [options.closeButtonSelector='[data-fcse-role=inlinemodal-close]']
     *  The selector for nodes inside of the modal that should act as close
     *  buttons.
     * @param {Boolean} [options.autoHide=true] Whether to auto-hide the modal
     *  if the user doesn't interact with it.
     * @param {Number} [options.autoHideDelay=5000] If autoHide is set to true,
     *  this defines the delay after which the modal is hidden.
     * @constructor
     * @name InlineModal
     */
    var InlineModal = function (selector, options) {
        EventDispatcher.apply(this, arguments);

        // Set elements
        this._elements = $(selector);

        // Set options
        var defaultOptions = {

            // The jQuery method to use to show the modal. Can be one of 'show' or 'fadeIn'.
            showMethod: 'fadeIn',

            // The duration for the show effect.
            showEffectDuration: 300,

            //The jQuery method to use to hide the modal. Can be one of 'hide' or 'fadeOut'.
            hideMethod: 'fadeOut',

            // The duration for the show effect.
            hideEffectDuration: 600,

            // The event type to listen to.
            triggerEventName: 'mouseenter',

            // The attribute that contains the selector to the trigger element.
            triggerSelectorAttribute: 'data-trigger',

            // Whether to hide the modal when the user leaves it with the mouse.
            hideOnLeave: true,

            // If hideOnLeave is set to true, this defines the duration by which hiding is delayed
            hideDelay: 300,

            // The selector for nodes inside of the modal that should act as close buttons.
            closeButtonSelector: '[data-fcse-role=inlinemodal-close]',

            // Whether to auto-hide the modal if the user doesn't interact with it.
            autoHide: true,

            // If autoHide is set to true, this defines the delay after which the modal is hidden.
            autoHideDelay: 5000
        };

        this.options = $.extend({}, defaultOptions, options);

        if (_.indexOf(validShowEffects, this.options.showMethod) == -1) {
            this.options.showMethod = defaultOptions.showMethod;
        }

        if (_.indexOf(validHideEffects, this.options.hideMethod) == -1) {
            this.options.hideMethod = defaultOptions.hideMethod;
        }

        // Register events
        this.registerEvent("show");
        this.registerEvent("hide");

        this._init();
    };

    InlineModal.prototype = /** @lends InlineModal */ $.extend(Object.create(EventDispatcher.prototype), {

        /**
         * A reference to the ctor
         *
         * @type {InlineModal}
         */
        constructor: InlineModal,

        /**
         * The applied options for this instance
         *
         * @type {Object}
         */
        options: null,

        /**
         * The internally used timeout id
         *
         * @type {Number}
         * @private
         */
        _closeTimeout: null,

        /**
         * Initialises matching InlineModals
         *
         * @private
         */
        _init: function () {
            this._elements.each($.proxy(this._initItem, this));
        },

        /**
         * Runs initialisation for a single matching node
         *
         * @param {Number} _ The index of the node in jQuery's node collection
         * @param {HTMLElement} node The matching node
         * @private
         */
        _initItem: function (_, node) {
            node = $(node);
            var triggerNodeSelector = node.attr(this.options.triggerSelectorAttribute),
                triggerNode = $(triggerNodeSelector),
                triggerEventName = this.options.triggerEventName,
                closeButtonSelector = this.options.closeButtonSelector;

            if (!triggerNode.length) {
                throw new Error('InlineModal cannot find trigger node, selector was ' + triggerNodeSelector);
            }

            triggerNode.on(triggerEventName, $.proxy(this.show, this, node));
            node.find(closeButtonSelector).on('click', $.proxy(this.hide, this, node));
        },

        /**
         * Shows the inline modal
         *
         * @param {jQuery} node The node of the modal
         */
        show: function (node) {
            if (this._currentNode != node) {
                // If there are multiple modals managed by this instance, hide
                // others before showing this one
                this._elements.hide();
            }

            clearTimeout(this._closeTimeout);
            this._currentNode = node;
            node[this.options.showMethod](this.options.showEffectDuration);

            if (this.options.hideOnLeave) {
                node.on('mouseenter', $.proxy(function(){
                    clearTimeout(this._closeTimeout);
                }, this));
                node.on('mouseleave', $.proxy(this._scheduleClose, this, node, this.options.hideDelay));
            }

            if (this.options.autoHide) {
                this._scheduleClose(node, this.options.autoHideDelay);
            }

            // Dispatch show event
            if (!node.data('inline-modal-open')){
                node.data('inline-modal-open', true);
                this.dispatchEvent("show", node);
            }
        },

        /**
         * Hides the modal
         *
         * @param {jQuery} node The node of the modal
         */
        hide: function (node) {
            if (this.options.hideOnLeave) {
                node.off('mouseenter')
                    .off('mouseleave');
            }
            clearTimeout(this._closeTimeout);
            node[this.options.hideMethod](this.options.hideEffectDuration);

            // Dispatch hide event
            this.dispatchEvent("hide", node);
            node.data('inline-modal-open', false);
        },

        /**
         * Schedules hiding of the modal
         *
         * @param {jQuery} node The node of the modal
         * @param {Number} delay The delay in ms
         * @private
         */
        _scheduleClose: function (node, delay) {
            this._closeTimeout = setTimeout($.proxy(this.hide, this, node), delay);
        }
    });

    return InlineModal;
});



// WEBPACK FOOTER //
// ./src/Allt/FrontendBundle/Resources/assets/components/inlinemodal/inlineModal.js